import { useQuery } from "@tanstack/react-query";
import { apiClient } from "src/dm/utils/axios";

/**
 * One family-membership entry. A retail item can belong to several
 * families; the legacy code held this set in the `additionalFamilyCodes`
 * dictionary on the SearchItem / AddRewardItem flow.
 *
 * `totalItemCount` and `memberRetailItemNumbers` were added so the
 * Add-Item dialog can compute "how many siblings aren't yet on the deal"
 * client-side (mirrors legacy `fullFamilyOnAdd` check on
 * `SearchItem.aspx.cs` line ~190+).
 */
export interface RetailItemFamily {
  familyCode: number;
  description: string | null;
  totalItemCount: number;
  memberRetailItemNumbers: number[];
}

interface Envelope {
  items: RetailItemFamily[];
  total: number;
}

const url = (retailItemNumber: number) =>
  `/dm/V1/retail-items/${retailItemNumber}/family-codes`;

/**
 * One-shot fetch — bypasses react-query so the family-prompt flow can
 * trigger the lookup imperatively (only when the user clicks "Add to
 * deal" on a row that has a family code). Keeps the search results from
 * eagerly triggering N round-trips. Normalises the response so older API
 * builds that don't yet return totalItemCount / memberRetailItemNumbers
 * still produce a valid prompt (they will show "0 missing" and the
 * prompt will collapse to a single-add — i.e. the legacy default).
 */
export const fetchRetailItemFamilies = async (
  retailItemNumber: number
): Promise<RetailItemFamily[]> => {
  const response = await apiClient.get<Envelope>(url(retailItemNumber));
  return (response.data.items ?? []).map((f) => ({
    familyCode: f.familyCode,
    description: f.description ?? null,
    totalItemCount: f.totalItemCount ?? 0,
    memberRetailItemNumbers: f.memberRetailItemNumbers ?? [],
  }));
};

/**
 * Fetch every family code a retail item is in. Used by the family-add
 * modal so the user can pick which family to expand when an item
 * participates in more than one. Mirrors `additionalFamilyCodes` from
 * legacy `Item/SearchItem.aspx.cs` and `Item/AddRewardItem.aspx.cs`.
 */
export const useRetailItemFamilies = (retailItemNumber: number | undefined) =>
  useQuery({
    queryKey: ["retail-item-families", retailItemNumber],
    enabled: retailItemNumber !== undefined && retailItemNumber > 0,
    queryFn: async (): Promise<RetailItemFamily[]> => {
      const response = await apiClient.get<Envelope>(
        url(retailItemNumber as number)
      );
      return response.data.items ?? [];
    },
  });
